import { ChildById } from "../ComponentRenderer";
import { ComponentProps } from "../registry";
import { readChildren, useBound } from "../helpers";
import { useStore } from "../../store";
import { readPath } from "../../path";
import { BoundValue } from "../../types";

export function List({ fields, scope }: ComponentProps) {
  const direction = useBound<string>(fields.direction as BoundValue, scope);
  const children = readChildren(fields);
  const className = `a2-list${direction === "horizontal" ? " dir-horizontal" : ""}`;

  if (children && "template" in children) {
    return (
      <TemplatedList
        className={className}
        dataBinding={children.template.dataBinding}
        templateId={children.template.componentId}
        outerScope={scope}
      />
    );
  }
  if (children && "explicitList" in children) {
    return (
      <div className={className}>
        {children.explicitList.map((id) => <ChildById key={id} id={id} scope={scope} />)}
      </div>
    );
  }
  return <div className={className} />;
}

function TemplatedList({
  className,
  dataBinding,
  templateId,
  outerScope,
}: {
  className: string;
  dataBinding: string;
  templateId: string;
  outerScope: unknown;
}) {
  // Subscribe to the bound array. When dataModel changes, re-renders only if
  // the resolved array differs by reference (Zustand strict compare).
  const items = useStore((s) => readPath(dataBinding, s.dataModel, outerScope));
  const pendingData = useStore((s) => s.pendingData);

  if (!Array.isArray(items) || items.length === 0) {
    if (pendingData) {
      return (
        <div className={className}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="a2-skeleton-card" />
          ))}
        </div>
      );
    }
    return <div className={className}><div className="a2-list-empty">No items.</div></div>;
  }

  return (
    <div className={className}>
      {items.map((item, idx) => {
        // Key by stable item identity when present so React keeps child state
        // with the right row across reorder / middle-remove (falls back to idx).
        const k = item && typeof item === "object" && "id" in item
          ? String((item as Record<string, unknown>).id)
          : idx;
        return <ChildById key={k} id={templateId} scope={item} />;
      })}
    </div>
  );
}
